"use client"

import { useState, useEffect } from "react"
import logo from "../../static/images/logo.jpeg"
import { useNavigate, useLocation } from "react-router-dom"

const Navbar = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [showServicesDropdown, setShowServicesDropdown] = useState(false)
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about-us" },
    { name: "Services", path: "/services", hasDropdown: true },
    { name: "Features", path: "/features" },
    { name: "Contact", path: "/contact-us" },
  ]

  const serviceLinks = [
    { name: "IMO Tank Container Certification", path: "/services/imo-tank-container" },
    { name: "Condition Survey Import/Export", path: "/services/condition-survey" },
    { name: "On/Off Hire Survey", path: "/services/on-off-hire-survey" },
    { name: "Pre/Post Repair Survey", path: "/services/pre-post-repair-survey" },
    { name: "Tank Inspection Report", path: "/services/tank-inspection-report" },
  ]

  const handleNavClick = (path) => {
    setIsMobileMenuOpen(false)
    navigate(path)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const isActivePath = (path) => location.pathname === path
  const isServicesActive = () => location.pathname.startsWith("/services")

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-headerbg shadow-lg backdrop-blur-md"
          : "bg-headerbg shadow-md"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8">
        <div
          className={`flex items-center justify-between transition-all duration-300 ease-in-out ${
            isScrolled ? "h-[70px]" : "h-[88px]"
          }`}
        >
          {/* Logo */}
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate("/")}
          >
            <img
              src={logo}
              alt="Naik Logistics"
              className={`transition-all duration-300 ${
                isScrolled ? "h-12" : "h-14"
              }`}
            />
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => {
              const isActive =
                link.name === "Services"
                  ? isServicesActive()
                  : isActivePath(link.path)

              return (
                <div key={link.path} className="relative group">
                  <button
                    onClick={() => handleNavClick(link.path)}
                    onMouseEnter={() =>
                      link.hasDropdown && setShowServicesDropdown(true)
                    }
                    onMouseLeave={() =>
                      link.hasDropdown && setShowServicesDropdown(false)
                    }
                    className={`transition-colors font-medium text-md relative uppercase tracking-[0.03rem] py-2 ${
                      isActive
                        ? "text-orange"
                        : "text-secondary hover:text-orange"
                    }`}
                  >
                    {link.name}

                    {/* Underline */}
                    <span
                      className={`absolute bottom-0 left-0 w-full h-0.5 bg-orange transform origin-left transition-transform duration-300 ${
                        isActive
                          ? "scale-x-100"
                          : "scale-x-0 group-hover:scale-x-100"
                      }`}
                    />
                  </button>

                  {/* Services Dropdown */}
                  {link.hasDropdown && (
                    <div className="absolute left-0 top-12 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                      <div className="relative">
                        {/* Arrow */}
                        <span
                          className="absolute -top-2 left-6 w-0 h-0
                          border-l-8 border-r-8 border-b-8
                          border-l-transparent border-r-transparent border-b-orange"
                        />

                        {/* Dropdown Box */}
                        <div className="w-80 bg-headerbg rounded-sm shadow-2xl py-3 px-2">
                          {serviceLinks.map((service, idx) => (
                            <button
                              key={idx}
                              onClick={() => handleNavClick(service.path)}
                              className={`group w-full text-left px-4 py-3 transition-colors border-b border-gray-300 last:border-b-0 ${
                                location.pathname === service.path
                                  ? "text-orange bg-gray-50"
                                  : "text-gray-700 hover:bg-gray-50 hover:text-orange"
                              }`}
                            >
                              {service.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}

            {/* Login */}
            <button
              onClick={() => navigate("/login")}
              className="px-6 py-2.5 bg-orange text-white font-semibold rounded-lg
              hover:bg-orange uppercase tracking-[0.03rem]
              transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Login
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden text-gray-700 hover:text-orange transition-colors p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            <div className="space-y-1">
              {navLinks.map((link) => {
                const isActive =
                  link.name === "Services"
                    ? isServicesActive()
                    : isActivePath(link.path)

                return (
                  <div key={link.path}>
                    {/* MAIN BUTTON */}
                    <button
                      onClick={() => {
                        if (link.hasDropdown) {
                          setIsMobileServicesOpen(!isMobileServicesOpen)
                        } else {
                          handleNavClick(link.path)
                        }
                      }}
                      className={`w-full flex items-center justify-between px-4 py-1 rounded-lg transition-colors font-medium ${
                        isActive
                          ? "bg-orange-50 text-orange"
                          : "text-gray-700 hover:bg-orange-50 hover:text-orange"
                      }`}
                    >
                      <span>{link.name}</span>

                      {link.hasDropdown && (
                        <svg
                          className={`w-4 h-4 transition-transform ${
                            isMobileServicesOpen ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      )}
                    </button>

                    {/* SERVICES SUB MENU */}
                    {link.hasDropdown && isMobileServicesOpen && (
                      <div className="pl-4 mt-2 space-y-1">
                        {serviceLinks.map((service, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleNavClick(service.path)}
                            className={`w-full text-left px-4 py-2 text-sm rounded transition-colors ${
                              location.pathname === service.path
                                ? "text-orange bg-orange-50"
                                : "text-gray-600 hover:text-orange hover:bg-orange-50"
                            }`}
                          >
                            {service.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}

              {/* Login */}
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false)
                  navigate("/login")
                }}
                className="w-full mt-4 px-6 py-3 bg-orange text-white font-semibold
                rounded-lg hover:bg-orange transition-all shadow-lg"
              >
                Login
              </button>
            </div>
          </div>
        )}

      </div>
    </nav>
  )
}

export default Navbar
